import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Checkbox,
    DialogContent,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import useSextforceMetricsAutoPromotions, {
    AutoPromotionSettings,
    PromotionPayload,
} from '../../../../hooks/useSextforceMetricsAutoPromotions';
import useSubscriber from '../../../../hooks/useSubscriber';
import useSubscriberMe from '../../../../hooks/useSubscriberMe';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

interface Props {
    open: boolean;
    onClose: () => void;
    audienceType: AudienceType;
    settings: AutoPromotionSettings | null;
}

type AudienceType = 'all' | 'expired' | 'both';
type PromoType = 'free-trial' | 'first-month';
type OfferLimit =
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | '10'
    | '20'
    | '30'
    | '40'
    | '50'
    | '60'
    | '70'
    | '80'
    | '90'
    | '100'
    | '0';
type DurationOption =
    | '0'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | '10'
    | '11'
    | '12'
    | '13'
    | '14'
    | '15'
    | '16'
    | '17'
    | '18'
    | '19'
    | '20'
    | '21'
    | '22'
    | '23'
    | '24'
    | '25'
    | '26'
    | '27'
    | '28'
    | '29'
    | '30';

// yeah I know there's easier ways to do this but with the power of AI I didn't have to do it.
const offerLimitOptions: { label: string; value: OfferLimit }[] = [
    { label: 'No Limits', value: '0' },
    { label: '1 subscriber', value: '1' },
    { label: '2 subscribers', value: '2' },
    { label: '3 subscribers', value: '3' },
    { label: '4 subscribers', value: '4' },
    { label: '5 subscribers', value: '5' },
    { label: '6 subscribers', value: '6' },
    { label: '7 subscribers', value: '7' },
    { label: '8 subscribers', value: '8' },
    { label: '9 subscribers', value: '9' },
    { label: '10 subscribers', value: '10' },
    { label: '20 subscribers', value: '20' },
    { label: '30 subscribers', value: '30' },
    { label: '40 subscribers', value: '40' },
    { label: '50 subscribers', value: '50' },
    { label: '60 subscribers', value: '60' },
    { label: '70 subscribers', value: '70' },
    { label: '80 subscribers', value: '80' },
    { label: '90 subscribers', value: '90' },
    { label: '100 subscribers', value: '100' },
];

const durationOptions: { label: string; value: DurationOption }[] = [
    { label: '1 day', value: '1' },
    { label: '2 days', value: '2' },
    { label: '3 days', value: '3' },
    { label: '4 days', value: '4' },
    { label: '5 days', value: '5' },
    { label: '6 days', value: '6' },
    { label: '7 days', value: '7' },
    { label: '8 days', value: '8' },
    { label: '9 days', value: '9' },
    { label: '10 days', value: '10' },
    { label: '11 days', value: '11' },
    { label: '12 days', value: '12' },
    { label: '13 days', value: '13' },
    { label: '14 days', value: '14' },
    { label: '15 days', value: '15' },
    { label: '16 days', value: '16' },
    { label: '17 days', value: '17' },
    { label: '18 days', value: '18' },
    { label: '19 days', value: '19' },
    { label: '20 days', value: '20' },
    { label: '21 days', value: '21' },
    { label: '22 days', value: '22' },
    { label: '23 days', value: '23' },
    { label: '24 days', value: '24' },
    { label: '25 days', value: '25' },
    { label: '26 days', value: '26' },
    { label: '27 days', value: '27' },
    { label: '28 days', value: '28' },
    { label: '29 days', value: '29' },
    { label: '30 days', value: '30' },
];

const SextforceMetricsPromoCreatePromoDialog = (props: Props) => {
    const { open, onClose, audienceType, settings } = props;

    const { data: subscriber, refetch: refetchSubscriber, isLoading: isSubscriberLoading } = useSubscriber();
    const { data: meInfo, refetch: refetchMeInfo, isLoading: isMeInfoLoading } = useSubscriberMe();
    const { enqueueSnackbar } = useSnackbar();

    const { createPromotion, getCurrentNewSubscriberPromotionSettings, isCreatePromoLoading, isPromoSettingsLoading } =
        useSextforceMetricsAutoPromotions();
    const [audience, setAudience] = useState<AudienceType>(audienceType);
    const [promoType, setPromoType] = useState<PromoType>('free-trial');
    const [offerLimit, setOfferLimit] = useState<OfferLimit>('10');
    const [offerExpiration, setOfferExpiration] = useState<DurationOption>('7');
    const [freeTrialDuration, setFreeTrialDuration] = useState<DurationOption>('7');
    const [message, setMessage] = useState('');
    const [autoRepeat, setAutoRepeat] = useState(false);
    const [discountPercent, setDiscountPercent] = useState<number>(5);

    // Trigger refetchMeInfo on mount to ensure we have the latest subscription price for discount calculations
    useEffect(() => {
        refetchMeInfo();
    }, [refetchMeInfo]);

    // for testing of subscription price. Commenting out for prod.
    // subscriber.ofSubscriptionPrice = 10;
    const MIN_PRICE = 3;

    // Compute max allowed discount based on $3 minimum price
    const { maxDiscount, discountOptions, effectivePrice } = useMemo(() => {
        const subscriptionPrice = meInfo?.me?.subscribePrice || subscriber?.ofSubscriptionPrice || 0;
        if (subscriptionPrice <= MIN_PRICE) {
            return {
                maxDiscount: 0,
                discountOptions: [] as number[],
                effectivePrice: subscriptionPrice,
            };
        }

        // maxPercent = floor((1 - 3 / price) * 100) rounded down to nearest 5, clamped to 90
        const rawMax = (1 - MIN_PRICE / subscriptionPrice) * 100;
        const steppedMax = Math.floor(rawMax / 5) * 5;
        const maxDiscount = Math.min(90, Math.max(0, steppedMax));

        const options: number[] = [];
        for (let p = 5; p <= maxDiscount; p += 5) {
            options.push(p);
        }

        const effective = subscriptionPrice * (1 - (discountPercent || 0) / 100);

        return {
            maxDiscount,
            discountOptions: options,
            effectivePrice: Number(effective.toFixed(2)),
        };
        // include discountPercent so effectivePrice recomputes when it changes
    }, [meInfo, subscriber.ofSubscriptionPrice, discountPercent]);

    const noValidDiscount = maxDiscount < 5;

    // Clamp discountPercent when `subscriber.ofSubscriptionPrice` or maxDiscount changes
    useEffect(() => {
        if (noValidDiscount) {
            setDiscountPercent(0);
        } else if (discountPercent < 5 || discountPercent > maxDiscount) {
            setDiscountPercent(maxDiscount);
        }
    }, [maxDiscount, noValidDiscount, discountPercent]);

    const handleCreateAutoPromotion = async () => {
        const payload: PromotionPayload = {
            discount: promoType === 'free-trial' ? 100 : discountPercent,
            message: message,
            finishDays: Number(offerExpiration),
            subscribeCounts: Number(offerLimit),
            subscribeDays: Number(freeTrialDuration),
            type: audience === 'both' ? ['all', 'expired'] : [audience],
        };

        const result = await createPromotion(payload, autoRepeat)
            .then(res => {
                enqueueSnackbar('Auto Promotion saved successfully', { variant: 'success', autoHideDuration: 3000 });

                refetchSubscriber();
                refetchMeInfo();

                return res;
            })
            .catch(err => {
                enqueueSnackbar(`Error creating Auto Promotion: ${err.message || err.toString()}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            });

        if (!result) return;

        onClose();
        // show happy snackbar
    };

    useEffect(() => {
        const getSettings = async () => {
            // const settings = await getCurrentNewSubscriberPromotionSettings();
            if (settings) {
                // const typeArray = settings.type.split('|');
                // const audienceType = typeArray.length > 1 ? 'both' : typeArray[0];
                setAudience(audience as AudienceType);
                setOfferLimit(settings.subscribeCounts as unknown as OfferLimit);
                setOfferExpiration(settings.finishDays as unknown as DurationOption);
                setMessage(settings.message);
                setAutoRepeat(settings.repeat);
                if (settings.discount === 100) {
                    // 100 means trial
                    setPromoType('free-trial');
                } else {
                    setPromoType('first-month');
                }
                setDiscountPercent(settings.discount);
                setFreeTrialDuration(settings.subscribeDays as unknown as DurationOption);
            }
        };
        getSettings();
    }, [settings]);

    return (
        <DialogAdaptive open={open} onClose={onClose}>
            <StyledCardTitleBar
                title={`Manage Auto Promo - ${audience === 'all' ? 'New Subscribers' : 'Expired Subscribers'}`}
                isLoading={isSubscriberLoading || isMeInfoLoading}
            />
            <DialogContent sx={{ pl: 1.5, pr: 1.5 }}>
                <Grid container flexGrow={1} spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        {/* <DialogContentText mb={1}>
                            {audience === 'all' ? 'New Subscribers ' : 'Expired Subscribers '}Auto Promotion Repeat is currently{' '}
                            {autoRepeat ? (
                                <span style={{ color: theme.palette.success.dark }}>running</span>
                            ) : (
                                <span style={{ color: theme.palette.error.main }}>not running</span>
                            )}
                        </DialogContentText> */}
                        <Typography variant="body1" color="text.secondary">
                            Setup a Promotional Campaign below. When the campaign hits either the subscriber limit or time limit
                            OnlyStruggles will restart it automatically for you!
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2}>
                            {/* Audience toggle 
                        <ToggleButtonGroup
                            exclusive
                            value={audience}
                            onChange={(_, val: AudienceType | null) => {
                            if (val) setAudience(val);
                            }}
                            sx={{
                            mb: 1.5,
                            "& .MuiToggleButton-root": {
                                textTransform: "none",
                                px: 2.5,
                                borderRadius: 999,
                                border: "1px solid",
                                borderColor: "divider",
                            },
                            "& .Mui-selected": {
                                bgcolor: "primary.main",
                                color: "#fff",
                                "&:hover": { bgcolor: "primary.dark" },
                            },
                            }}
                        >
                            <ToggleButton value="all">New subscribers</ToggleButton>
                            <ToggleButton value="expired">Expired subscribers</ToggleButton>
                            <ToggleButton value="both">Both new and expired</ToggleButton>
                        </ToggleButtonGroup>*/}

                            {/* Promo type toggle */}
                            <ToggleButtonGroup
                                fullWidth
                                exclusive
                                value={promoType}
                                onChange={(_, val: PromoType | null) => {
                                    if (val) setPromoType(val);
                                }}
                            >
                                <ToggleButton value="free-trial">Free Trial</ToggleButton>
                                <ToggleButton
                                    value="first-month"
                                    sx={{
                                        minWidth: '200px',
                                    }}
                                >
                                    First Month Discount
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {/* Dropdowns */}
                            <Stack spacing={2}>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    label="Offer limit"
                                    value={offerLimit}
                                    onChange={e => setOfferLimit(e.target.value as OfferLimit)}
                                >
                                    {offerLimitOptions.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    label="Offer expiration"
                                    value={offerExpiration}
                                    onChange={e => setOfferExpiration(e.target.value as DurationOption)}
                                >
                                    <MenuItem key="0" value="0">
                                        No Expiration
                                    </MenuItem>
                                    {durationOptions.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {promoType === 'free-trial' ? (
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Free trial duration"
                                        value={freeTrialDuration}
                                        onChange={e => setFreeTrialDuration(e.target.value as DurationOption)}
                                    >
                                        {durationOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Discount Percent"
                                        value={discountPercent || ''}
                                        onChange={e => setDiscountPercent(Number(e.target.value) || 0)}
                                        disabled={noValidDiscount}
                                        helperText={
                                            noValidDiscount
                                                ? `Current price ($${(meInfo?.me?.subscribePrice || subscriber.ofSubscriptionPrice).toFixed(
                                                      2,
                                                  )}) is already at or near the $${MIN_PRICE.toFixed(2)} minimum.`
                                                : `Discounted first-month price: $${effectivePrice.toFixed(2)}`
                                        }
                                    >
                                        {discountOptions.map(pct => (
                                            <MenuItem key={pct} value={pct}>
                                                {pct}%
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Stack>

                            {/* Message box with counter */}
                            <Box>
                                <TextField
                                    label="Message (optional)"
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    value={message}
                                    onChange={
                                        e => setMessage(e.target.value.slice(0, 500)) // hard cap
                                    }
                                    inputProps={{ maxLength: 500 }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {message.length}/500
                                    </Typography>
                                </Box>
                            </Box>
                            <FormControlLabel
                                control={<Checkbox checked={autoRepeat} onChange={e => setAutoRepeat(e.target.checked)} />}
                                label="Auto Repeat Promo Once It's Finished"
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>

            <StyledDialogActions
                sx={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                }}
            >
                <Button
                    color="secondary"
                    size="medium"
                    onClick={() => {
                        onClose();
                    }}
                >
                    Close
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="medium"
                    loading={isCreatePromoLoading}
                    disabled={!subscriber}
                    onClick={() => {
                        handleCreateAutoPromotion();
                    }}
                >
                    <span>Save</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceMetricsPromoCreatePromoDialog;
